import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  AccessibilityState,
  KeyboardShortcut,
  // ScreenReaderAnnouncement,
  // KEYBOARD_SHORTCUTS,
} from '../types/music';

interface AccessibilityContextType {
  state: AccessibilityState;
  announceToScreenReader: (
    message: string,
    priority?: 'polite' | 'assertive',
  ) => void;
  setKeyboardNavigationMode: (enabled: boolean) => void;
  registerShortcut: (shortcut: KeyboardShortcut) => () => void;
  unregisterShortcut: (action: string) => void;
  setCurrentContext: (context: string | null) => void;
  focusSkipTarget: (target: string) => void;
}

type AccessibilityAction =
  | { type: 'SET_HIGH_CONTRAST'; payload: boolean }
  | { type: 'SET_REDUCED_MOTION'; payload: boolean }
  | { type: 'SET_SCREEN_READER_ACTIVE'; payload: boolean }
  | { type: 'SET_KEYBOARD_NAVIGATION'; payload: boolean }
  | { type: 'SET_CURRENT_FOCUS'; payload: string | null }
  | { type: 'ADD_ANNOUNCEMENT'; payload: string }
  | { type: 'CLEAR_ANNOUNCEMENTS' }
  | { type: 'SET_SKIP_TARGET'; payload: string | null };

const initialState: AccessibilityState = {
  isHighContrast: false,
  prefersReducedMotion: false,
  screenReaderActive: false,
  keyboardNavigationMode: false,
  currentFocus: null,
  skipLinkTarget: null,
  announcements: [],
  lastAnnouncement: null,
};

function accessibilityReducer(
  state: AccessibilityState,
  action: AccessibilityAction,
): AccessibilityState {
  switch (action.type) {
    case 'SET_HIGH_CONTRAST':
      return { ...state, isHighContrast: action.payload };
    case 'SET_REDUCED_MOTION':
      return { ...state, prefersReducedMotion: action.payload };
    case 'SET_SCREEN_READER_ACTIVE':
      return { ...state, screenReaderActive: action.payload };
    case 'SET_KEYBOARD_NAVIGATION':
      return { ...state, keyboardNavigationMode: action.payload };
    case 'SET_CURRENT_FOCUS':
      return { ...state, currentFocus: action.payload };
    case 'ADD_ANNOUNCEMENT':
      return {
        ...state,
        announcements: [...state.announcements, action.payload],
        lastAnnouncement: action.payload,
      };
    case 'CLEAR_ANNOUNCEMENTS':
      return { ...state, announcements: [] };
    case 'SET_SKIP_TARGET':
      return { ...state, skipLinkTarget: action.payload };
    default:
      return state;
  }
}

const AccessibilityContext = createContext<
  AccessibilityContextType | undefined
>(undefined);

interface AccessibilityProviderProps {
  children: React.ReactNode;
}

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(accessibilityReducer, initialState);
  const shortcutsRef = useRef<Map<string, KeyboardShortcut>>(new Map());
  const currentContextRef = useRef<string | null>(null);
  const announcementTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Detect system preferences
  useEffect(() => {
    const mediaQueryHighContrast = window.matchMedia(
      '(prefers-contrast: high)',
    );
    const mediaQueryReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    );

    const updateHighContrast = () => {
      dispatch({
        type: 'SET_HIGH_CONTRAST',
        payload: mediaQueryHighContrast.matches,
      });
    };

    const updateReducedMotion = () => {
      dispatch({
        type: 'SET_REDUCED_MOTION',
        payload: mediaQueryReducedMotion.matches,
      });
    };

    // Initial detection
    updateHighContrast();
    updateReducedMotion();

    // Listen for changes
    mediaQueryHighContrast.addEventListener('change', updateHighContrast);
    mediaQueryReducedMotion.addEventListener('change', updateReducedMotion);

    return () => {
      mediaQueryHighContrast.removeEventListener('change', updateHighContrast);
      mediaQueryReducedMotion.removeEventListener(
        'change',
        updateReducedMotion,
      );
    };
  }, []);

  // Detect screen reader usage
  useEffect(() => {
    // Simple heuristic for screen reader detection
    const checkScreenReader = () => {
      const hasScreenReaderElement = document.querySelector(
        '[aria-live], [role="status"], [role="alert"]',
      );
      const hasAriaDescriptions = document.querySelector(
        '[aria-describedby], [aria-labelledby]',
      );

      if (hasScreenReaderElement || hasAriaDescriptions) {
        dispatch({ type: 'SET_SCREEN_READER_ACTIVE', payload: true });
      }
    };

    // Check after a short delay to allow DOM to populate
    const timer = setTimeout(checkScreenReader, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Keyboard event handler
  const handleKeyboardEvent = useCallback(
    (event: KeyboardEvent) => {
      const { key, ctrlKey, metaKey, shiftKey, altKey } = event;

      // Enable keyboard navigation mode on Tab key
      if (key === 'Tab') {
        if (!state.keyboardNavigationMode) {
          dispatch({ type: 'SET_KEYBOARD_NAVIGATION', payload: true });
        }
      }

      // Check for registered shortcuts
      for (const [, shortcut] of shortcutsRef.current) {
        const modifiersMatch =
          (shortcut.modifiers?.includes('ctrl') || false) ===
            (ctrlKey || metaKey) &&
          (shortcut.modifiers?.includes('shift') || false) === shiftKey &&
          (shortcut.modifiers?.includes('alt') || false) === altKey;

        const keyMatch = key.toLowerCase() === shortcut.key.toLowerCase();

        if (keyMatch && modifiersMatch) {
          // Check context
          if (
            shortcut.context &&
            shortcut.context !== currentContextRef.current
          ) {
            continue;
          }

          if (shortcut.preventDefault) {
            event.preventDefault();
          }

          if (shortcut.stopPropagation) {
            event.stopPropagation();
          }

          shortcut.handler();
          break;
        }
      }
    },
    [state.keyboardNavigationMode],
  );

  // Set up keyboard event listeners
  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardEvent);

    // Disable keyboard navigation mode on mouse use
    const handleMouseEvent = () => {
      if (state.keyboardNavigationMode) {
        dispatch({ type: 'SET_KEYBOARD_NAVIGATION', payload: false });
      }
    };

    document.addEventListener('mousedown', handleMouseEvent);
    document.addEventListener('click', handleMouseEvent);

    return () => {
      document.removeEventListener('keydown', handleKeyboardEvent);
      document.removeEventListener('mousedown', handleMouseEvent);
      document.removeEventListener('click', handleMouseEvent);
    };
  }, [handleKeyboardEvent, state.keyboardNavigationMode]);

  const announceToScreenReader = useCallback(
    (message: string, priority: 'polite' | 'assertive' = 'polite') => {
      // Clear previous timeout
      if (announcementTimeoutRef.current) {
        clearTimeout(announcementTimeoutRef.current);
      }

      dispatch({ type: 'ADD_ANNOUNCEMENT', payload: message });

      // Create or update live region
      let liveRegion = document.getElementById('accessibility-live-region');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = 'accessibility-live-region';
        liveRegion.setAttribute('aria-live', priority);
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.style.position = 'absolute';
        liveRegion.style.left = '-10000px';
        liveRegion.style.width = '1px';
        liveRegion.style.height = '1px';
        liveRegion.style.overflow = 'hidden';
        document.body.appendChild(liveRegion);
      }

      // Update the live region
      liveRegion.setAttribute('aria-live', priority);
      liveRegion.textContent = message;

      // Clear the announcement after a delay
      announcementTimeoutRef.current = setTimeout(() => {
        dispatch({ type: 'CLEAR_ANNOUNCEMENTS' });
      }, 5000);
    },
    [],
  );

  const setKeyboardNavigationMode = useCallback((enabled: boolean) => {
    dispatch({ type: 'SET_KEYBOARD_NAVIGATION', payload: enabled });
  }, []);

  const registerShortcut = useCallback((shortcut: KeyboardShortcut) => {
    shortcutsRef.current.set(shortcut.action, shortcut);

    // Return unregister function
    return () => {
      shortcutsRef.current.delete(shortcut.action);
    };
  }, []);

  const unregisterShortcut = useCallback((action: string) => {
    shortcutsRef.current.delete(action);
  }, []);

  const setCurrentContext = useCallback((context: string | null) => {
    currentContextRef.current = context;
  }, []);

  const focusSkipTarget = useCallback(
    (target: string) => {
      const element = document.getElementById(target);
      if (element) {
        element.focus();
        dispatch({ type: 'SET_SKIP_TARGET', payload: target });
        announceToScreenReader(`Jumped to ${target}`, 'assertive');
      }
    },
    [announceToScreenReader],
  );

  const contextValue: AccessibilityContextType = {
    state,
    announceToScreenReader,
    setKeyboardNavigationMode,
    registerShortcut,
    unregisterShortcut,
    setCurrentContext,
    focusSkipTarget,
  };

  return (
    <AccessibilityContext.Provider value={contextValue}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = (): AccessibilityContextType => {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error(
      'useAccessibility must be used within an AccessibilityProvider',
    );
  }
  return context;
};

export default AccessibilityContext;
